'use client';

import {createContext, forwardRef, useCallback, useState} from 'react';

import {useUpdateEffect} from 'common';
import {Dropdown} from 'Dropdown';

import {SelectInput} from './SelectInput';
import {SelectList} from './SelectList';
import {useSelect} from './hook';

import type {
  SelectContextProps,
  SelectProps,
  SingleSelectProps,
  SelectMultipleValue,
  SelectListOnChange,
} from './types';

export const SelectContext = createContext<SelectContextProps>({} as never);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Select = forwardRef<HTMLInputElement, SelectProps<any>>((props, baseRef) => {
  const {ref, inputProps, context, dropdownProps, components} = useSelect(props, baseRef);

  const Input = components?.input || SelectInput;
  const List = components?.list || SelectList;

  return (
    <SelectContext.Provider value={context}>
      <Input ref={ref} {...inputProps} />

      <Dropdown {...dropdownProps}>
        <List components={components} />
      </Dropdown>
    </SelectContext.Provider>
  );
});

export const SingleSelect = forwardRef<HTMLInputElement, SingleSelectProps>((props, ref) => {
  const [value, setValue] = useState<SelectMultipleValue>(() => (props.value ? [props.value] : []));

  const onChange = useCallback<SelectListOnChange>(
    (newValue, ...rest) => {
      props.onChange(newValue[0], ...rest);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onChange]
  );

  useUpdateEffect(() => {
    setValue(props.value ? [props.value] : []);
  }, [props.value]);

  return <Select ref={ref} {...props} value={value} onChange={onChange} />;
});
